/**
 * ProjectVaultRow — bottom of Subs & Inbox tab: one-click full-project download.
 * Monochrome ink tokens only (R-1 contractor UI).
 */
import { useState } from 'react'
import { downloadProjectVault } from '../../../api/projects'
import { track } from '../../../lib/track'
import CoLogTree from '../../../features/pulse/CoLogTree'

interface Props {
  projectId: number
  documentCount: number
}

export default function ProjectVaultRow({ projectId, documentCount }: Props) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const subtitle = documentCount > 0
    ? `${documentCount} document${documentCount === 1 ? '' : 's'} · every file for this project, one download`
    : 'No documents yet — sub uploads, compliance docs, pay apps and statements will collect here.'

  async function onDownload() {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await downloadProjectVault(projectId)
      track('project_vault_downloaded', { project_id: projectId })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div data-testid="project-vault-section" style={{ marginTop: 14, marginBottom: 18 }}>
    <div
      className="panel"
      data-testid="project-vault-row"
      style={{
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        border: '1px solid var(--line)',
        background: 'var(--surface)',
      }}
    >
      <div
        aria-hidden
        style={{
          width: 36,
          height: 36,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 16,
          color: 'var(--ink3)',
          border: '1px solid var(--line)',
          borderRadius: 6,
          background: 'var(--surface)',
        }}
      >
        ☰
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>
          Project Vault
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 2, lineHeight: 1.4 }}>
          {subtitle}
        </div>
        {error ? (
          <div style={{ fontSize: 11, color: 'var(--ink2)', marginTop: 4 }}>{error}</div>
        ) : null}
      </div>
      <button
        type="button"
        className="btn b-dk btn-sm"
        data-testid="project-vault-download"
        disabled={busy}
        onClick={() => void onDownload()}
        style={{ flexShrink: 0 }}
      >
        {busy ? '…' : 'Download'}
      </button>
    </div>
    <CoLogTree projectId={projectId} compact />
    </div>
  )
}
